/**
 * Estado do módulo de nível dos rios (Zustand). Registra o codec de URL `h`
 * no import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 *
 * A chave é `h`, de hidrologia: `r` já é a radiossonda.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { Dados, Estilo, Situacao } from './types';

export const DEFAULT_ESTILO: Estilo = {
  // 7 px, e não 5: medido no enquadramento inicial, que mostra a Amazônia
  // Legal inteira, um ponto de 5 px some entre as nuvens e os rótulos das
  // cidades (6,5 até 09/10/2026; 7 é o tamanho base da qualidade do ar e dos
  // pluviômetros, padronizados a pedido do usuário).
  tamanho: 7,
  opacidade: 0.95,
  // Estação sem referência aparece por padrão: ela está medindo, e esconder
  // o que se mede por não termos com o que comparar seria perder informação.
  mostrarSemReferencia: true,
  mostrarTendencia: true,
};

export const LIMITES = {
  tamanho: { min: 3, max: 12, step: 0.5 },
  opacidade: { min: 0.2, max: 1, step: 0.05 },
} as const;

/** A camada começa desligada: é dado extra, não o assunto principal do app. */
export const DEFAULT_VISIVEL = false;

const situacaoInicial: Situacao = { estado: 'idle', mensagem: null, erro: null };

const DADOS_VAZIOS: Dados = {
  rios: [],
  t0: 0,
  passoMin: 60,
  slots: 48,
  gerado: null,
  fonte: null,
  comReferencia: 0,
};

export interface RiverState {
  visivel: boolean;
  estilo: Estilo;
  situacao: Situacao;
  dados: Dados;

  setVisivel: (on: boolean) => void;
  setEstilo: (patch: Partial<Estilo>) => void;
  setSituacao: (s: Situacao) => void;
  setDados: (d: Dados) => void;
}

export const useRiverStore = create<RiverState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  estilo: DEFAULT_ESTILO,
  situacao: situacaoInicial,
  dados: DADOS_VAZIOS,

  setVisivel: (on) => set({ visivel: on }),
  setEstilo: (patch) => set((s) => ({ estilo: { ...s.estilo, ...patch } })),
  setSituacao: (situacao) => set({ situacao }),
  setDados: (dados) => set({ dados }),
}));

/**
 * `h=1` liga a camada; as letras seguintes guardam só o que difere do padrão
 * (`t` tamanho, `o` opacidade, `s` inverte "mostrar sem referência",
 * `d` inverte o chevron de tendência). Ex.: `h=1t7s`.
 *
 * Como nos outros módulos, as chaves booleanas significam "o contrário do
 * padrão", nunca um valor fixo — assim o link continua curto e correto se o
 * padrão mudar.
 */
export function encodeRivers(s: Pick<RiverState, 'visivel' | 'estilo'>): string | null {
  if (!s.visivel) return null;
  let out = '1';
  if (s.estilo.tamanho !== DEFAULT_ESTILO.tamanho) out += `t${s.estilo.tamanho}`;
  if (s.estilo.opacidade !== DEFAULT_ESTILO.opacidade) out += `o${s.estilo.opacidade}`;
  if (s.estilo.mostrarSemReferencia !== DEFAULT_ESTILO.mostrarSemReferencia) out += 's';
  if (s.estilo.mostrarTendencia !== DEFAULT_ESTILO.mostrarTendencia) out += 'd';
  return out;
}

export function decodeRivers(value: string): Partial<Pick<RiverState, 'visivel' | 'estilo'>> | null {
  if (!value || value[0] !== '1') return null;
  const estilo: Estilo = { ...DEFAULT_ESTILO };

  const tamanho = /t(\d+(?:\.\d+)?)/.exec(value);
  if (tamanho) estilo.tamanho = limitar(Number(tamanho[1]), LIMITES.tamanho.min, LIMITES.tamanho.max, DEFAULT_ESTILO.tamanho);

  const opac = /o(\d*\.?\d+)/.exec(value);
  if (opac) estilo.opacidade = limitar(Number(opac[1]), LIMITES.opacidade.min, LIMITES.opacidade.max, DEFAULT_ESTILO.opacidade);

  if (value.includes('s')) estilo.mostrarSemReferencia = !DEFAULT_ESTILO.mostrarSemReferencia;
  if (value.includes('d')) estilo.mostrarTendencia = !DEFAULT_ESTILO.mostrarTendencia;

  return { visivel: true, estilo };
}

function limitar(n: number, min: number, max: number, padrao: number): number {
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : padrao;
}

registerUrlCodec({
  key: 'h',
  encode: () => encodeRivers(useRiverStore.getState()),
  decode: (value) => {
    const s = decodeRivers(value);
    if (s) useRiverStore.setState(s);
  },
  subscribe: (onChange) =>
    useRiverStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.estilo !== b.estilo) onChange();
    }),
});
